<template>
  <SearchBarOverlay
    :groups="groups"
    :has-no-results="hasNoResults"
    :has-recent-search="actor.snapshot.value.context.recentSearch.length > 0"
    :is-loading="actor.snapshot.value.matches('fetchRequiredData')"
    :is-loading-next-page="actor.snapshot.value.matches('fetchNextPageData')"
    :is-open="isVisible"
    :search-query="actor.snapshot.value.context.searchQuery"
    :selected-group="actor.snapshot.value.context.groupBy"
    @close="handleClose"
    @input="handleQueryChange"
    @infinity-scroll="actor.send({ type: 'FETCH_SEARCH_NEXT_PAGE' })"
    @group-by="handleGroupBy"
  >
    <SearchBarRecentSearch
      v-if="actor.snapshot.value.context.searchQuery === '' && actor.snapshot.value.context.recentSearch.length > 0"
      :recent-search="actor.snapshot.value.context.recentSearch"
      @recent-search-click="(searchQuery) => actor.send({ type: 'ON_SEARCH_QUERY', searchQuery })"
    />

    <SearchBarCustomers
      v-if="actor.snapshot.value.context.groupBy === SEARCH_BAR_GROUP.CUSTOMERS"
      :customers="actor.snapshot.value.context.customers.data"
      @view-profile="handleViewProfile"
    />

    <SearchBarProducts
      v-if="actor.snapshot.value.context.groupBy === SEARCH_BAR_GROUP.PRODUCTS"
      :products="actor.snapshot.value.context.products.data"
      @view-product-details="handleOpenProductReport"
    />
  </SearchBarOverlay>

  <ProductReport
    :app-id-product-id="selectedAppIdProductId"
    :is-open="selectedAppIdProductId !== null && selectedTrackerProductId !== null"
    :tracker-product-id="selectedTrackerProductId"
    @close="handleCloseProductReport"
  />
</template>

<script lang="ts" setup>
import { debounce } from "lodash";
import SearchBarOverlay from "./SearchBarOverlay.vue";
import { useMachine } from "@xstate-5/vue";
import { searchBarMachine, symbolSearchBarMachine } from "./SearchBar.machine";
import { inspector } from "vue-app/utils/xstate";
import { computed, onMounted, onUnmounted, provide, ref, watch } from "vue";
import SearchBarRecentSearch from "./SearchBarRecentSearch.vue";
import SearchBarCustomers from "./SearchBarCustomers/SearchBarCustomers.vue";
import SearchBarProducts from "vue-app/components/SearchBar/SearchBarProducts/SearchBarProducts.vue";
import { SEARCH_BAR_GROUP } from "vue-app/components/SearchBar/SearchBar.constants";
import ProductReport from "vue-app/components/ProductReport/ProductReport.vue";

interface Props {
  isOpen: boolean;
}

interface Emits {
  close: [];
  previewCustomer: [customerId: number];
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();
const isVisible = ref(props.isOpen);
const timeoutKeys = ref<ReturnType<typeof setTimeout>[]>([]);

const selectedAppIdProductId = ref<string | null>(null);
const selectedTrackerProductId = ref<string | null>(null);

const actor = useMachine(searchBarMachine, { inspect: inspector().inspect });
provide(symbolSearchBarMachine, actor);

const groups = computed(() => {
  return [
    {
      label: "Customers",
      group: SEARCH_BAR_GROUP.CUSTOMERS,
      count: actor.snapshot.value.context.customers.total ?? 0,
      disabled: actor.snapshot.value.context.customers.total === 0,
    },
    {
      label: "Products",
      group: SEARCH_BAR_GROUP.PRODUCTS,
      count: actor.snapshot.value.context.products.total ?? 0,
      disabled: actor.snapshot.value.context.products.total === 0,
    },
  ];
});

const hasNoResults = computed(() => {
  if (actor.snapshot.value.context.groupBy === SEARCH_BAR_GROUP.CUSTOMERS) {
    return actor.snapshot.value.context.customers.data.length === 0;
  }

  if (actor.snapshot.value.context.groupBy === SEARCH_BAR_GROUP.PRODUCTS) {
    return actor.snapshot.value.context.products.data.length === 0;
  }

  return false;
});

onMounted(() => {
  window.addEventListener("keydown", onEscKeyPress);
});

onUnmounted(() => {
  window.removeEventListener("keydown", onEscKeyPress);

  timeoutKeys.value.forEach((timeoutKey) => {
    clearTimeout(timeoutKey);
  });
});

const onEscKeyPress = (event: KeyboardEvent) => {
  if (event.key === "Escape") {
    handleClose();
  }
};

const handleClose = () => {
  isVisible.value = false;
  emit("close");
};

const handleQueryChange = debounce((query: string) => {
  actor.send({ type: "ON_SEARCH_QUERY", searchQuery: query });
}, 350);

const handleGroupBy = (group: SEARCH_BAR_GROUP) => {
  actor.send({ type: "GROUP_BY", groupBy: group });
};

const handleBackToLastViewedItem = (itemId: string) => {
  const timeoutKey = setTimeout(() => {
    const el = document.querySelector(`[data-item-id="${itemId}"]`);

    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, 1000);

  timeoutKeys.value.push(timeoutKey);
};

const handleViewProfile = (customerId: number) => {
  emit("previewCustomer", customerId);
};

const handleOpenProductReport = (appIdProductId: string, trackerProductId: number) => {
  selectedAppIdProductId.value = appIdProductId;
  selectedTrackerProductId.value = trackerProductId.toString();
  isVisible.value = false;
};

const handleCloseProductReport = () => {
  isVisible.value = true;
  handleBackToLastViewedItem(selectedTrackerProductId.value || "");
  selectedAppIdProductId.value = null;
  selectedTrackerProductId.value = null;
};

watch(
  () => props.isOpen,
  (newValue) => {
    isVisible.value = newValue;
  }
);
</script>
